import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PencilLine, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { eurExact, monthLabel } from "./format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export const InvoicedOverrideButton = ({ month, override, autoValue, autoBasis, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    setValue(override ? String(override.invoiced_value) : "");
    setNote(override?.note || "");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const n = Number(String(value).replace(/[€,\s]/g, ""));
    if (!Number.isFinite(n)) { toast.error("Enter a valid € amount"); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/sales/invoiced-override/${month}`, { invoiced_value: n, note }, authHeaders());
      toast.success(`Invoiced for ${monthLabel(month, "short")} set to ${eurExact(n)}`);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/sales/invoiced-override/${month}`, authHeaders());
      toast.success(`Back to the automatic figure for ${monthLabel(month, "short")}`);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={openDialog} className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 ring-1 ring-white/10 hover:bg-white/5 hover:text-white" title="Manually set this month's invoiced figure" aria-label="Edit invoiced figure" data-testid="invoiced-override-btn">
        <PencilLine className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-slate-900 text-slate-100 sm:max-w-md" data-testid="invoiced-override-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Invoiced · {monthLabel(month)}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Type the month's invoiced net (ex-VAT) figure. It replaces the automatic figure on the card and trend chart until you clear it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Net € amount</span>
              <input autoFocus inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 104767.50" className="mt-1.5 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2.5 font-mono text-lg text-slate-50 outline-none focus:border-emerald-400" data-testid="invoiced-override-input" />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Note <span className="font-normal normal-case tracking-normal text-slate-500">(optional)</span></span>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="e.g. Per Sage month-end" className="mt-1.5 w-full rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-sm text-slate-50 outline-none focus:border-emerald-400" data-testid="invoiced-override-note" />
            </label>
            <p className="text-xs text-slate-500" data-testid="invoiced-override-auto">
              Automatic figure ({autoBasis === "report" ? "invoice report" : "status change"}): <span className="font-mono text-slate-300">{eurExact(autoValue)}</span>
            </p>
            <div className="flex items-center justify-between gap-2 pt-1">
              {override ? (
                <button type="button" onClick={clear} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-rose-300 hover:bg-rose-500/10 disabled:opacity-60" data-testid="invoiced-override-clear">
                  <Trash2 className="h-4 w-4" /> Clear · use automatic
                </button>
              ) : <span />}
              <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-emerald-400 disabled:opacity-60" data-testid="invoiced-override-save">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
